'use client';

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { cn, resolveNativeButton } from '#lib/utils';

function Checkbox({
  className,
  nativeButton,
  ...props
}: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary data-indeterminate:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground dark:data-indeterminate:bg-primary relative flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors outline-none group-has-disabled/field:opacity-50 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3',
        className,
      )}
      nativeButton={nativeButton ?? resolveNativeButton(props.render)}
      // Every Sheet in this app is a Base UI Drawer under the hood (see
      // packages/ui/src/shadcn/sheet.tsx), and a Drawer's swipe-to-dismiss
      // gesture captures the pointer on ANY pointerdown whose target doesn't
      // match its hardcoded ignore list (`button,a,input,select,textarea,
      // label,[role="button"]`) — which does NOT include `[role="checkbox"]`,
      // since this Checkbox renders a plain `<span role="checkbox">`, not a
      // `<button>`. That pointer capture redirects the subsequent pointerup
      // and click to the Drawer's own content container instead of the
      // checkbox (PAY-3212 — clicking directly on the box did nothing, while
      // clicking its associated <label> text worked, because `label` IS on
      // that ignore list and forwards the click via `htmlFor`). Same fix as
      // PAY-3211's Switch: set Base UI's `data-base-ui-swipe-ignore` opt-out
      // by default so every checkbox stays clickable inside a Sheet/Drawer.
      data-base-ui-swipe-ignore=""
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="group/checkbox-indicator grid place-content-center text-current transition-none [&>svg]:size-3.5"
      >
        {/* Indeterminate shows a dash, not a check (Radix-era behavior). */}
        <CheckIcon className="group-data-indeterminate/checkbox-indicator:hidden" />
        <MinusIcon className="hidden group-data-indeterminate/checkbox-indicator:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
